<!--
  Faixa discreta logo abaixo da barra de filtros, acima dos cards. Some quando nada pede ação, mesmo havendo
  documento só de histórico (card 3708).
  `ignore-export` na faixa inteira: é estado transitório da tela, não conteúdo do relatório.
-->
@if (actionableDocuments().length) {
  <div class="processing-notice ignore-export" role="status">
    <i
      class="pi pi-sync processing-notice__icon"
      [class.processing-notice__icon--spinning]="hasActiveWork()"
      aria-hidden="true"></i>
    <span class="processing-notice__text">{{ summary() }}</span>
    <button
      type="button"
      class="processing-notice__toggle"
      aria-haspopup="dialog"
      (click)="panel.toggle($event)">
      Ver documentos
      <i class="pi pi-chevron-down" aria-hidden="true"></i>
    </button>
  </div>
}

<p-popover #panel [appendTo]="'body'" styleClass="processing-notice-panel">
  <div class="processing-menu">
    <div class="processing-menu__section-label">Documentos fora da Análise Econômica</div>
    <p class="processing-menu__hint">
      Enquanto não forem validados, cada ano continua mostrando o documento que já valia antes.
    </p>

    <ul class="processing-menu__list">
      @for (doc of actionableDocuments(); track doc.item.id) {
        <li class="processing-menu__item">
          <div class="processing-menu__name" [title]="doc.item.name">{{ doc.item.name }}</div>
          <div class="processing-menu__meta">
            <span class="processing-menu__stage processing-menu__stage--{{ doc.stage }}">
              {{ stageLabel[doc.stage] }}
            </span>
            <span class="processing-menu__since">{{ sinceLabel(doc) }}</span>
          </div>
          @if (progressText(doc); as detail) {
            <div class="processing-menu__detail" [title]="detail">{{ detail }}</div>
          }
          <app-accounting-progress-percent
            class="processing-menu__percent"
            [summary]="doc.item.progress" />
          @if (hasProgress(doc)) {
            <button
              type="button"
              class="processing-menu__expand"
              [attr.aria-expanded]="expandedId() === doc.item.id"
              [attr.aria-controls]="'processing-timeline-' + doc.item.id"
              (click)="toggleTimeline(doc)">
              <i
                class="pi"
                [class.pi-chevron-right]="expandedId() !== doc.item.id"
                [class.pi-chevron-down]="expandedId() === doc.item.id"
                aria-hidden="true"></i>
              {{ expandedId() === doc.item.id ? 'Esconder andamento' : 'Ver andamento' }}
            </button>
            @if (expandedId() === doc.item.id) {
              <app-accounting-progress-timeline
                class="processing-menu__timeline"
                [attr.id]="'processing-timeline-' + doc.item.id"
                variant="compact"
                [companyId]="doc.item.companyId || companyId"
                [extractionId]="doc.item.id"
                [summary]="doc.item.progress"
                (settled)="onTimelineSettled()" />
            }
          }
          @if (canOpenStandardization() || (canOpenDataRoom() && doc.item.dataRoomDocumentId)) {
            <div class="processing-menu__links">
              @if (canOpenStandardization()) {
                <!--
                  Link de verdade para a Padronização com o documento aberto: Ctrl/Cmd+clique, clique do meio e
                  "Abrir link em nova guia" levam à tela completa em nova guia. O clique simples é interceptado e
                  abre o documento na MESMA modal do "Ver De/Para", sem sair da Análise Econômica.
                -->
                <a
                  class="processing-menu__link"
                  data-testid="processing-standardization-link"
                  [href]="standardizationHref(doc)"
                  (click)="onStandardizationDocumentClick($event, doc)">
                  Abrir na Padronização
                </a>
              }
              @if (canOpenDataRoom() && doc.item.dataRoomDocumentId; as dataRoomDocumentId) {
                <!-- Link direto sem a pasta: a tela do Data Room descobre a pasta e abre o painel no "Andamento". -->
                <a
                  class="processing-menu__link"
                  data-testid="processing-data-room-link"
                  [routerLink]="dataRoomRoute"
                  [queryParams]="dataRoomQueryParams(dataRoomDocumentId)">
                  Abrir no Data Room
                </a>
              }
            </div>
          }
        </li>
      }
    </ul>

    <!--
      Histórico (card 3708): documentos não validados cujo período já vale por outro documento validado, que foram
      substituídos ou perderam o arquivo de origem. A classificação vem do backend (`analysisSituation`). Recolhido
      por padrão; aberto, cada um mostra a tag "Histórico" e a frase do backend no lugar da etapa, sem barra de
      progresso nem "Ver andamento", porque não há nada a acompanhar nem a aprovar.
    -->
    @if (historicalDocuments().length) {
      <div class="processing-menu__history">
        <button
          type="button"
          class="processing-menu__history-toggle"
          data-testid="processing-history-toggle"
          [attr.aria-expanded]="historyExpanded()"
          aria-controls="processing-history-list"
          (click)="toggleHistory()">
          <i
            class="pi"
            [class.pi-chevron-right]="!historyExpanded()"
            [class.pi-chevron-down]="historyExpanded()"
            aria-hidden="true"></i>
          Histórico ({{ historicalDocuments().length }}), não precisam de ação
        </button>

        @if (historyExpanded()) {
          <ul class="processing-menu__list" id="processing-history-list">
            @for (doc of historicalDocuments(); track doc.item.id) {
              <li class="processing-menu__item" data-testid="processing-history-item">
                <div class="processing-menu__name" [title]="doc.item.name">{{ doc.item.name }}</div>
                <div class="processing-menu__meta">
                  <span class="processing-menu__stage processing-menu__stage--historical">
                    Histórico
                  </span>
                </div>
                <div class="processing-menu__situation">{{ historicalMessage(doc) }}</div>
                @if (canOpenStandardization()) {
                  <div class="processing-menu__links">
                    <a
                      class="processing-menu__link"
                      data-testid="processing-standardization-link"
                      [href]="standardizationHref(doc)"
                      (click)="onStandardizationDocumentClick($event, doc)">
                      Abrir na Padronização
                    </a>
                  </div>
                }
              </li>
            }
          </ul>
        }
      </div>
    }

    @if (canOpenStandardization()) {
      <div class="processing-menu__divider"></div>
      <a
        class="processing-menu__footer-link"
        [routerLink]="standardizationRoute"
        [queryParams]="listQueryParams()"
        (click)="onStandardizationListClick($event)">
        Ver no back-office
        <i class="pi pi-arrow-right" aria-hidden="true"></i>
      </a>
    }
  </div>
</p-popover>
